import { Component, useCallback, useEffect, useState } from 'react';
import { css, keyframes } from '@emotion/react';
import useEmblaCarousel from 'embla-carousel-react';

import Header from '../atoms/Header';
import BodyText from '../atoms/BodyText';
import Hstack3CellExtremity from '../atoms/Hstack3CellExtremity';
import InlinedRichText from './InlinedRichText';
import { defaultColors } from '../../../constants/colors';
import * as Breakpoints from '../../../constants/breakpoints';
import * as InternalPropTypes from '../../../constants/internal-types';
import {
  BackgroundColorEnum,
  TypeSEOWeightTypes,
} from '../../../constants/cms-constants';
import { getLowerSEOWeight, getSEOWeight } from '../../../utils/SEOWeight';
import IndexSpinner from '../atoms/IndexSpinner';
import Hstack2Cell from '../atoms/Hstack2Cell';
import ArrowButton from '../atoms/ArrowButton';

const animations = {
  headerHighlightUp: keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: -100% 0;
  }
  `,
  headerHighlightDown: keyframes`
   0% {
    background-position: -0% 0;
  }
  100% {
    background-position: -100% 0;
  }
  `,
  headerHighlightLeaveUp: keyframes`
  0% {
    background-position: -100% 0;
  }
  100% {
    background-position: -0% 0;
    visibility: hidden;
  }
  `,
  headerHighlightLeaveDown: keyframes`
  0% {
    background-position: -100% 0;
  }
  100% {
    background-position: -200% 0;
    visibility: hidden;
  }
  `,
};

const animationTime = '1300ms';

const styles = css({
  '& .section-title': {
    color: defaultColors.lightHeader,
    zIndex: '2',
  },
  '& .body-container': {
    marginTop: '20px',
    marginBottom: '20px',
    '& p': {
      marginBottom: 0,
    },
    '& ul': {
      paddingInlineStart: '28px',
      WebkitPaddingStart: '28px',
    },
  },
  '& .headerText': {
    marginLeft: 'auto',
    width: 'fit-content',
    marginBottom: '8px',
    cursor: 'pointer',
    position: 'relative',
    color: defaultColors.lightHeader,
    '&:focus': {
      outline: 'none',
    },
    '&.activeDown, &.activeUp, &.prevActiveDown, &.prevActiveUp': {
      '&::before': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        backgroundImage: `linear-gradient(to right, ${BackgroundColorEnum.WHITE} 50%, ${BackgroundColorEnum.BLACK} 50%)`,
        height: '1px',
        width: '100%',
        backgroundPosition: '-100% 0',
        backgroundSize: '200% auto',
      },
    },
    '&.activeDown': {
      pointerEvents: 'none',
      '&::before': {
        animation: `${animations.headerHighlightDown} ${animationTime} forwards`,
      },
    },
    '&.activeUp': {
      pointerEvents: 'none',
      '&::before': {
        animation: `${animations.headerHighlightUp} ${animationTime} forwards`,
      },
    },
    '&.prevActiveDown': {
      '&::before': {
        animation: `${animations.headerHighlightLeaveDown} ${animationTime} forwards`,
      },
    },
    '&.prevActiveUp': {
      '&::before': {
        animation: `${animations.headerHighlightLeaveUp} ${animationTime} forwards`,
      },
    },
  },

  [Breakpoints.Mobile.mq]: {
    '& .current-value-indicator': {
      '& .item-right': {
        display: 'none',
      },
      '& .item-left': {
        position: 'absolute',
      },
    },
    '& .bottom-controller .item-right': {
      display: 'none',
    },
    '& .top-controller .item-left': {
      display: 'none',
    },
  },
  [Breakpoints.TabletUp.mq]: {
    '& .top-controller': {
      display: 'none',
    },
  },
  '.value-carousel': {
    marginTop: '30px',
    [Breakpoints.TabletUp.mq]: {
      marginTop: '-80px',
      overflow: 'visible',
    },
  },
  '.embla__viewport': {
    overflow: 'hidden',
    [Breakpoints.Mobile.mq]: {
      marginBottom: '30px',
    },
    [Breakpoints.Tablet.mq]: {
      marginTop: '25px',
      marginBottom: '25px',
      paddingRight: '150px',
    },
    [Breakpoints.DesktopUp.mq]: {
      marginTop: '15px',
      marginBottom: '15px',
      paddingLeft: '100px',
    },
  },
  '.embla__container': {
    backfaceVisibility: 'hidden',
    display: 'flex',

    touchAction: 'pan-x',
    flexDirection: 'column',
    [Breakpoints.Mobile.mq]: {
      height: '200px',
      alignItems: 'center',
      textAlign: 'left',
    },
    [Breakpoints.Tablet.mq]: {
      height: '250px',
      alignItems: 'center',
      textAlign: 'left',
    },
    [Breakpoints.DesktopUp.mq]: {
      height: '290px',
      width: '80%',
      alignItems: 'left',
    },
  },
  '.embla__slide': {
    flex: '0 0 100%',
    [Breakpoints.Mobile.mq]: {
      maxWidth: '540px',
    },
    [Breakpoints.Tablet.mq]: {
      maxWidth: '570px',
    },
  },
  '.embla-thumbs__slide': {
    flex: '0 0 22%',
  },
  '.embla-thumbs__slide__number': {
    border: '0px',
    background: 'transparent',
  },
  '.embla-thumbs__container': {
    flexDirection: 'column',
  },
});

type ValueCarouselProps = {
  lightHeader: InternalPropTypes.RichText;
  items: InternalPropTypes.MediaItem[];
  seoWeight: TypeSEOWeightTypes;
};

function ValueCarousel(props: ValueCarouselProps) {
  const { lightHeader, items, seoWeight } = props;
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'center',
    axis: 'y',
  });

  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    watchDrag: false,
  });

  const [currentIndex, setCurrentIndex] = useState(0);
  const [prevIndex, setPrevIndex] = useState(items.length - 1);

  const onThumbClick = useCallback(
    (index: number) => {
      if (!emblaApi || !emblaThumbsApi) return;
      emblaApi.scrollTo(index);
    },
    [emblaApi, emblaThumbsApi],
  );

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
  }, [emblaApi]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
  }, [emblaApi]);

  const onScroll = useCallback((emblaApi) => {
    setPrevIndex(
      emblaApi.previousScrollSnap() === emblaApi.selectedScrollSnap()
        ? prevIndex
        : emblaApi.previousScrollSnap(),
    );
    setCurrentIndex(emblaApi.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onScroll(emblaApi);
    emblaApi.on('reInit', onScroll);
    emblaApi.on('scroll', onScroll);
  }, [emblaApi, onScroll]);

  const addSEOWeight = getSEOWeight(seoWeight, seoWeight);
  const lowerSEOWeight = getLowerSEOWeight(seoWeight);

  const getAnimationClass = (actualPrevIndex, index) => {
    const maxIndex = items.length - 1;
    switch (index) {
      case currentIndex:
        if (currentIndex - prevIndex === -maxIndex) {
          return 'activeDown';
        }
        if (currentIndex - prevIndex === maxIndex) {
          return 'activeUp';
        }
        if (currentIndex - prevIndex < 0) {
          return 'activeUp';
        }
        if (currentIndex - prevIndex > 0) {
          return 'activeDown';
        }
        return '';
      case actualPrevIndex:
        if (currentIndex - prevIndex === -maxIndex) {
          return 'prevActiveDown';
        }
        if (currentIndex - prevIndex === maxIndex) {
          return 'prevActiveUp';
        }
        if (currentIndex - prevIndex < 0) {
          return 'prevActiveUp';
        }
        if (currentIndex - prevIndex > 0) {
          return 'prevActiveDown';
        }
        return '';
      default:
        return '';
    }
  };
  return (
    <div className="value-carousel-molecule" css={styles}>
      <div className="current-value-indicator">
        <Hstack3CellExtremity
          onRenderLeftCell={() => (
            <BodyText type={BodyText.types.STATIC16} className="section-title">
              {lightHeader}
            </BodyText>
          )}
          onRenderRightCell={() => (
            <div className="embla-thumbs">
              <div className="embla-thumbs__viewport" ref={emblaThumbsRef}>
                <div className="embla-thumbs__container">
                  {items.map((item, idx) => (
                    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
                    <div
                      className="embla-thumbs__slide embla-thumbs__slide__number"
                      onClick={() => onThumbClick(idx)}
                      key={idx}
                    >
                      <BodyText
                        as={lowerSEOWeight}
                        type={BodyText.types.LIGHT14}
                        className={`headerText ${getAnimationClass(
                          prevIndex,
                          idx,
                        )}`}
                      >
                        {item.header}
                      </BodyText>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        />
      </div>
      <div className="top-controller">
        <Hstack3CellExtremity
          onRenderRightCell={() => (
            <Hstack2Cell
              className="buttons"
              onRenderLeftCell={() => (
                <ArrowButton onClick={onPrevButtonClick} direction="left" />
              )}
              onRenderRightCell={() => (
                <ArrowButton onClick={onNextButtonClick} />
              )}
            />
          )}
        />
      </div>
      <div className="value-carousel">
        <div className="embla__viewport" ref={emblaRef}>
          <div className="embla__container">
            {items.map((mediaItem) => (
              <div className="embla__slide" key={mediaItem.name}>
                <Header seoWeight={addSEOWeight} type={Header.types.VALUES}>
                  {mediaItem.header}
                </Header>
                <div className="body-container">
                  <InlinedRichText richText={mediaItem.bodyText} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Hstack3CellExtremity
        className="paged-carousel-cells2 bottom-controller"
        onRenderLeftCell={() => (
          <div>
            <IndexSpinner
              currentIndex={currentIndex + 1}
              maxIndex={items.length}
            />
          </div>
        )}
        onRenderRightCell={() => (
          <Hstack2Cell
            className="buttons"
            onRenderLeftCell={() => (
              <ArrowButton onClick={onPrevButtonClick} direction="left" />
            )}
            onRenderRightCell={() => (
              <ArrowButton onClick={onNextButtonClick} />
            )}
          />
        )}
      />
    </div>
  );
}

export default ValueCarousel;
